import { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { base64UrlDecode } from './pkce';

/**
 * Gets the username from the current Cognito session.
 * For native users, username is the email.
 * For SSO users, username is in the format {IdPName}_{sub}.
 *
 * @returns Promise<string> The username from the ID token
 * @throws Error if the session cannot be retrieved or token cannot be decoded
 */
export async function getUsernameFromToken(): Promise<string> {
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken().getJwtToken();
    const tokenPayload = JSON.parse(base64UrlDecode(idToken.split('.')[1]));
    return tokenPayload['cognito:username'] || tokenPayload.sub;
  } catch (error) {
    console.error('Failed to get username from token:', error);
    throw new Error('Failed to get username from token');
  }
}

const SSO_LOGOUT_KEY = 'sso_logout';

interface SsoLogoutMarker {
  endpoint: string; // Cognito Hosted-UI /logout endpoint
  clientId: string; // app client id used for this SSO login
}

/**
 * Persist what's needed to terminate the Cognito Hosted-UI session at logout.
 * Called during SSO login. `authorizeEndpoint` is the Cognito authorize URL
 * (.../oauth2/authorize); the logout endpoint is its sibling (.../logout).
 */
export function storeSsoLogout(authorizeEndpoint: string, clientId: string): void {
  const endpoint = authorizeEndpoint.replace(/\/oauth2\/authorize\/?$/, '/logout');
  localStorage.setItem(SSO_LOGOUT_KEY, JSON.stringify({ endpoint, clientId }));
}

/** Clear the SSO logout marker (after logout teardown). */
function clearSsoLogout(): void {
  localStorage.removeItem(SSO_LOGOUT_KEY);
}

function readSsoLogout(): SsoLogoutMarker | null {
  const raw = localStorage.getItem(SSO_LOGOUT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SsoLogoutMarker>;
    if (parsed.endpoint && parsed.clientId) {
      return { endpoint: parsed.endpoint, clientId: parsed.clientId };
    }
  } catch {
    // Corrupt marker — treat as no SSO session.
  }
  return null;
}

/**
 * Whether the current session was established via a federated IdP (SSO).
 * Cognito populates an `identities` claim in the ID token for federated users;
 * native (username/password) sessions don't have it. Deriving this from the
 * live token — rather than a stored flag — keeps logout correct regardless of
 * stale markers or concurrent tabs.
 *
 * Returns `null` when the session can't be read (expired/refresh hiccup, network
 * blip, Amplify error) so callers can distinguish "cleanly native" from
 * "unknown" and avoid silently downgrading a real SSO user to a native logout.
 */
async function isFederatedSession(): Promise<boolean | null> {
  try {
    const session = await Auth.currentSession();
    const payload = JSON.parse(base64UrlDecode(session.getIdToken().getJwtToken().split('.')[1]));
    return Array.isArray(payload.identities) && payload.identities.length > 0;
  } catch {
    return null;
  }
}

/**
 * Log the user out. Revokes tokens globally at Cognito (clears them on this
 * device and invalidates the refresh token server-side). For federated (SSO)
 * sessions, returns the Cognito Hosted-UI logout URL the caller should navigate
 * to so the SSO session cookie is cleared; returns null for native sessions.
 *
 * Federation is read from the current session token, so a stale marker left by
 * an abandoned SSO attempt never misroutes a native user. If the session can't
 * be read, fall back to the marker: a native user has no marker, so
 * marker + unreadable-session is a strong SSO signal, and clearing the SSO
 * cookie is the safe default for a logout.
 */
export async function performLogout(logoutUri: string): Promise<string | null> {
  const marker = readSsoLogout();
  const federated = await isFederatedSession();
  try {
    await Auth.signOut({ global: true });
  } catch (error) {
    // Proceed with logout even if global revocation fails (e.g. expired token).
    console.error('Global sign-out failed; continuing with logout', error);
  }
  clearSsoLogout();
  const isSso = federated === true || (federated === null && marker !== null);
  if (!isSso || !marker) return null;
  const params = new URLSearchParams({ client_id: marker.clientId, logout_uri: logoutUri });
  return `${marker.endpoint}?${params.toString()}`;
}

/**
 * Custom hook to get the username from the current Cognito session.
 * Returns the username once it's available, or undefined while loading.
 */
export function useUsername(): string | undefined {
  const [username, setUsername] = useState<string | undefined>(undefined);

  useEffect(() => {
    getUsernameFromToken()
      .then(setUsername)
      .catch((error) => {
        console.error('Failed to get username:', error);
      });
  }, []);

  return username;
}
